import React, { useEffect, useRef, useState } from 'react';
import First from 'components/quote/normal/create/First';
import PageTemplate from 'components/template/PageTemplate';
import Second from 'components/quote/normal/create/Second';
import Container from 'components/common/Container';
import Third from 'components/quote/normal/create/Third';
import Fourth from 'components/quote/normal/create/Fourth';
import { useAtom, useSetAtom } from 'jotai';
import { containerFileAtom, quoteFileAtom, quoteInfoAtom } from 'atom/quote';
import usePreventRefresh from 'hook/usePreventRefresh';
import { useNavigate } from 'react-router-dom';
import CustomButton from 'components/common/CustomButton';
import Dialog from 'components/common/Dialog';
import { IContainerList, IPackingList, IQuote } from 'types/quote';
import { hsCodePopAtom, hsCodeResultAtom } from 'atom/hsCode';
import HsCodeModal from 'components/common/modal/HsCodeModal';
import AddressModal from 'components/common/modal/AddressModal';
import useCheckCompany from 'hook/useCheckCompany';
import { ReactComponent as FileList } from 'assets/common/fileList.svg'
import { toast } from 'react-toastify';
import { validateContainerList, validatePackingItem, validatePackingList } from 'components/function';

const NormalCreate = () => {
    const navigate = useNavigate();
    const [quoteInfo, setQuoteInfo] = useAtom(quoteInfoAtom)
    const [popHsCodeOpen, setPoHsCodepOpen] = useAtom(hsCodePopAtom)
    const setHsCodeResult = useSetAtom(hsCodeResultAtom);
    const [quoteFile, setQuoteFile] = useAtom(quoteFileAtom)
    const setContainerFile = useSetAtom(containerFileAtom);
    const [popWeightOpen, setPopWeightOpen] = useState<boolean>(false);
    const [popDangerListOpen, setPopDangerListOpen] = useState<boolean>(false);
    const [dangerFileArr, setDangerFileArr] = useState<File[]>([]);
    const [popDangerOpen, setPopDangerOpen] = useState<boolean>(false);
    const [popDelivery, setPopDelivery] = useState<boolean>(false);
    const [addressType, setAddressType] = useState<'inland' | 'delivery' | ''>('');
    const [containerInfo, setContainerInfo] = useState<IContainerList>({} as IContainerList);
    const [packingInfo, setPackingInfo] = useState<IPackingList>({} as IPackingList);

    const step1Ref = useRef<HTMLDivElement | null>(null);
    const step2Ref = useRef<HTMLDivElement | null>(null);

    usePreventRefresh()
    useCheckCompany();

    useEffect(() => {
        const temp = window.sessionStorage.getItem('normal')
        const type = window.sessionStorage.getItem('type')
        if (temp) {
            const info: IQuote = JSON.parse(temp)
            setQuoteInfo(info)
            if (info.step1.transportType === 'AIR' && type === 'main') {
                setPackingInfo(p => ({
                    ...p,
                    weight: Number(info.amount)
                }))
            }
            setTimeout(() => {
                window.sessionStorage.removeItem('normal')
                window.sessionStorage.removeItem('type')
            }, 2000)
        }
        else {
            setQuoteInfo({} as IQuote)
            setContainerFile([])
            setQuoteFile([])
            setHsCodeResult('')
            setTimeout(() => {
                setQuoteInfo({
                    step1: { tradeType: 'EXPORT', transportType: 'SEA' },
                    step2: { shipmentMode: 'FCL-DRY' }
                } as IQuote);
            }, 200)
        }
    }, [])

    const goConfirm = () => {
        const { step1, step2, isShippingDate } = quoteInfo;

        const transportType = step1?.transportType;
        const isSea = transportType === 'SEA';
        const isAir = transportType === 'AIR';

        const cond2 = !!step1?.tradeType;
        const cond3 = !!transportType;
        const cond4 = !!step1?.originId;
        const cond5 = !!step1?.destinationId;
        const cond6 = !!step2?.hsCode;
        const cond7 = typeof isShippingDate === 'boolean'
        const cond19 = !isShippingDate || !!step2?.shippingDate;
        const cond8 = !!step2?.incoterms;
        const cond9 = !!step2?.productNameKr;
        const cond10 = !!step2?.productNameEn;

        const cond11 = typeof step2?.packingRequired === 'boolean';
        const cond12 = typeof step2?.isDirect === 'boolean';
        const cond13 =
            transportType === 'SEA'
                ? step2?.shipmentMode === 'LCL' || typeof step2?.warehouseRequired === 'boolean'
                : true;
        const cond14 = !step2?.incoterms?.includes('D') || (!!step2?.deliveryAddress && !!step2?.deliveryAddressDetail);
        const cond15 = !(step1?.transportType === 'SEA' && step2?.carrierRequired && !step2?.preferredCarrier);
        const cond16 =
            step1?.transportType === 'SEA'
                ? step2?.isShipperName
                    ? !!step2?.shipperName
                    : true
                : true;
        const cond17 = !step2?.inlandTransportRequired || (!!step2?.inlandAddress && !!step2?.inlandAddressDetail);
        const cond18 =
            step1?.transportType === 'SEA'
                ? (typeof step2?.carrierRequired === 'boolean' &&
                    (!step2?.carrierRequired || !!step2?.preferredCarrier))
                : true;

        const conditions = [
            { target: step1Ref, pass: cond2, msg: '구간정보 구간을 선택해주세요.' },
            { target: step1Ref, pass: cond3, msg: '운송수단이 없습니다.' },
            { target: step1Ref, pass: cond4, msg: '출발지를 선택해주세요.' },
            { target: step1Ref, pass: cond5, msg: '도착지를 선택해주세요.' },
            { target: step2Ref, pass: cond7, msg: '출고일여부를 선택해주세요.' },
            { target: step2Ref, pass: cond19, msg: '출고일을 선택해주세요.' },
            { target: step2Ref, pass: cond6, msg: 'HS Code가 없습니다.' },
            { target: step2Ref, pass: cond9, msg: '품목명(한글)을 입력해주세요.' },
            { target: step2Ref, pass: cond10, msg: '품목명(영문)을 입력해주세요.' },
            { target: step2Ref, pass: cond8, msg: '인코텀즈를 선택해주세요.' },
            { target: step2Ref, pass: cond14, msg: '입고지 주소가 필요합니다.' },
            { target: step2Ref, pass: cond12, msg: '직항 여부가 지정되지 않았습니다.' },
            { target: step2Ref, pass: cond18, msg: '선호선사를 선택해주세요.' },
            { target: step2Ref, pass: cond15, msg: '선호 운송사를 입력해주세요.' },
            { target: step2Ref, pass: cond16, msg: '실화주명이 없습니다.' },
            { target: step2Ref, pass: cond13, msg: '창고 여부를 선택해주세요.' },
            { target: step2Ref, pass: cond17, msg: '내륙운송 주소가 필요합니다.' },
            { target: step2Ref, pass: cond11, msg: '포장 필요 여부가 지정되지 않았습니다.' },
        ];

        if (isSea) {
            const { shipmentMode, containerList, packingList, warehouseRequired } = step2;

            switch (shipmentMode) {
                case 'FCL-DRY':
                case 'OT':
                    // 컨테이너
                    conditions.push({
                        pass: !!(() => {
                            if (containerInfo.containerType &&
                                containerInfo.containerWeight &&
                                containerInfo.containerQuantity) {

                                switch (containerInfo.require) {
                                    case 'flexi':
                                        return typeof containerInfo.flexiBag === 'boolean';
                                    case 'frozen':
                                        return !!(containerInfo.tempMin && containerInfo.tempMax);
                                }

                                // 위험물 체크는 require와 상관없이 항상 검증
                                if (containerInfo.isDangerous) {
                                    return !!(containerInfo.dangerFile && containerInfo.dangerFile.length > 0);
                                }

                                // 기본 컨테이너 정보만으로 충분하면 true
                                return true;
                            } else {
                                return validateContainerList(containerList);
                            }
                        })(),
                        msg: '컨테이너 필수 값을 입력해주세요.',
                        target: step2Ref
                    });

                    // 포장
                    conditions.push({
                        pass: !!(() => {
                            if (step2?.warehouseRequired) {
                                if (quoteFile.length > 0) {
                                    return true;
                                }
                                else if (validatePackingItem(packingInfo, shipmentMode === 'OT')) {
                                    return true;
                                }
                                else {
                                    return validatePackingList(step2?.packingList);
                                }
                            }
                            return true;
                        })(),
                        msg: '포장 정보(파일 또는 목록)를 입력해주세요.',
                        target: step2Ref
                    });

                    break;

                case 'LCL':
                    conditions.push({
                        pass: !!(
                            quoteFile.length > 0 ||
                            validatePackingItem(packingInfo) ||
                            validatePackingList(packingList)
                        ),
                        msg: '포장정보 또는 첨부파일이 필요합니다.',
                        target: step2Ref
                    });
                    break;

                case 'FR':
                    if (warehouseRequired) {
                        conditions.push({
                            pass: !!(
                                quoteFile.length > 0 ||
                                validatePackingItem(packingInfo) ||
                                validatePackingList(packingList)
                            ),
                            msg: '포장정보 또는 첨부파일이 필요합니다.',
                            target: step2Ref
                        });
                    }
                    break;

                default:
                    conditions.push({ pass: false, msg: '유효하지 않은 선적 모드입니다.', target: step2Ref });
            }
        } else if (isAir) {
            conditions.push({
                pass: quoteFile.length > 0 || validatePackingItem(packingInfo) || validatePackingList(step2?.packingList),
                msg: '포장정보 또는 첨부파일이 필요합니다.',
                target: step2Ref
            });
        }

        const failed = conditions.find(c => !c.pass);
        if (failed) {
            scrollToDestination(failed.target)
            toast.error(failed.msg);
            return;
        }

        let tempQuoteInfo = { ...quoteInfo };

        // 컨테이너 처리 (데이터만)
        if (containerInfo.containerType &&
            containerInfo.containerWeight &&
            containerInfo.containerQuantity) {
            const { dangerFile, ...rest } = containerInfo;
            setContainerFile((prev) => [...prev, dangerFile ?? []]);

            tempQuoteInfo = {
                ...tempQuoteInfo,
                step2: {
                    ...tempQuoteInfo.step2!,
                    containerList: [...(tempQuoteInfo.step2?.containerList ?? []), rest],
                },
            };
        }

        // 패킹 처리 (데이터만)
        if (validatePackingItem(packingInfo)) {
            tempQuoteInfo = {
                ...tempQuoteInfo,
                step2: {
                    ...tempQuoteInfo.step2!,
                    packingList: [...(tempQuoteInfo.step2?.packingList ?? []), packingInfo],
                },
            };
            setPackingInfo({} as IPackingList);
        }

        // CBM / Weight / ChargeableWeight 합산
        const packingList = tempQuoteInfo?.step2?.packingList ?? [];
        let cbmSum = 0, weightSum = 0, cwSum = 0;

        packingList.forEach(arr => {
            if (arr.width && arr.height && arr.length) {
                cbmSum += (arr.width * arr.height * arr.length) / (100 ** 3);
            }
            if (arr.weight && arr.quantity) {
                weightSum += (arr.weight * Number(arr.quantity));
            }
            if (arr.chargeableWeight) {
                cwSum += arr.chargeableWeight;
            }
        });

        // 최종 quoteInfo 업데이트
        tempQuoteInfo = {
            ...tempQuoteInfo,
            cbm: cbmSum,
            totalWeight: weightSum,
            totalCW: cwSum,
        };

        // 최종 state 반영 및 저장
        setQuoteInfo(tempQuoteInfo);
        window.sessionStorage.setItem('normal', JSON.stringify(tempQuoteInfo));
        setContainerInfo({} as IContainerList);
        navigate('/quote/normal/confirm');
    }

    const scrollToDestination = (ref: React.RefObject<HTMLDivElement>) => {
        if (ref.current) {
            const top = ref.current.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: top - 80, // 80px 만큼 위로 여유 공간 확보
                behavior: "smooth",
            });
        }
    };

    return (
        <PageTemplate background='bg-[#F8F8FA]'>
            <Container className="flex flex-col gap-6 mb-6 pt-21">
                {/* {JSON.stringify(quoteInfo)} */}
                <First quoteInfo={quoteInfo} setQuoteInfo={setQuoteInfo} />
                <div ref={step1Ref}></div>
                <Second quoteInfo={quoteInfo} setQuoteInfo={setQuoteInfo} />
                <div ref={step2Ref}></div>
                <Third
                    quoteInfo={quoteInfo}
                    setQuoteInfo={setQuoteInfo}
                    setPopWeightOpen={setPopWeightOpen}
                    setPopDangerListOpen={setPopDangerListOpen}
                    setPopDangerOpen={setPopDangerOpen}
                    setDangerFileArr={setDangerFileArr}
                    setPopDelivery={setPopDelivery}
                    setAddressType={setAddressType}
                    containerInfo={containerInfo}
                    setContainerInfo={setContainerInfo}
                    setPackingInfo={setPackingInfo}
                    packingInfo={packingInfo}
                />
                <Fourth quoteInfo={quoteInfo} setQuoteInfo={setQuoteInfo} />

                <CustomButton
                    className='bg-primary mx-auto w-[200px]'
                    title='견적받기'
                    onClick={goConfirm}
                />
            </Container>
            <Dialog
                isOpen={popWeightOpen}
                setIsOpen={setPopWeightOpen}
                btnText='확인'
            >
                <p className='text-center'>
                    <span className='font-semibold'>운임산출중량 (Chargeable Weight)</span>는 실제중량(Actual Weight)과<br />
                    용적중량(Volume Weight) 둘 중 큰 쪽으로 산정됩니다.
                </p>
            </Dialog>
            <Dialog
                isOpen={popDangerListOpen}
                setIsOpen={setPopDangerListOpen}
                btnText='닫기'
            >
                {
                    dangerFileArr &&
                    dangerFileArr.map((item, idx) => (
                        <div
                            className='relative flex items-center gap-3 mb-3 px-6 border border-primary rounded h-10 text-primary text-sm'
                            key={idx}>
                            <FileList />
                            {item.name}
                        </div>
                    ))
                }
            </Dialog>
            <Dialog
                isOpen={popDangerOpen}
                setIsOpen={setPopDangerOpen}
                btnText='확인'
            >
                <p className='font-semibold text-center'>
                    위험물 선택시 <span className='text-red'>MSDS 파일첨부</span> 또는 요청사항에 <span className='text-red'>UN Number 및 Class</span> 등
                    정보를 반드시 입력하여 주시기 바랍니다.
                </p>
            </Dialog>
            <HsCodeModal
                isOpen={popHsCodeOpen}
                setIsOpen={setPoHsCodepOpen}
            />
            <AddressModal
                isOpen={popDelivery}
                setIsOpen={setPopDelivery}
                setQuoteInfo={setQuoteInfo}
                addressType={addressType}
            />
        </PageTemplate>
    )
}

export default NormalCreate